import React, { useState, useEffect, useRef } from 'react';
import scStyled from 'styled-components';
import { Avatar, Typography, Grid, CircularProgress, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';
import { useAppSelector, selectRooms, selectTypes, selectReservations, selectPreloadData, selectMessages, selectAuth,fetchMessages,useAppDispatch, selectMessagesLoading } from '@redux';
import { useMessageOperations } from 'src/Utils/Hooks/useScheduleMessages';
import { PassChangeButton } from '../Profile';
import default_avatar from "@assets/Images/default_avatar.jpg";
import { linkAvatar } from "@utils";
import { ReactComponent as IconFileImage } from '@assets/Icons/IconFileImage.svg';
import { ReactComponent as IconFileFile } from '@assets/Icons/IconFileFile.svg';
import { ReactComponent as IconPlus } from '@assets/Icons/IconPlus.svg';
import { ReservationOccurrence } from 'src/types';
import PrivateEventMark from '../Common/PrivateEventMark';

import { EditorContent, useEditor, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Mention from '@tiptap/extension-mention';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import RenderMessageContent from 'src/Utils/RenderMessageContent';

// Component props interface
interface ReservationInfoProps {
  reservationId: number;
  reservationOccurrence?: ReservationOccurrence;
  handleEdit: (range: "this" | "future" | "all" | null) => void;
  handleClose?: () => void;
}

// Message input state interface
interface MessageInputState {
  content: string;
  file: File | null;
}

// Main component
export const ReservationInfo: React.FC<ReservationInfoProps> = ({ 
  reservationId, 
  reservationOccurrence,
  handleEdit,
  handleClose 
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Get current user
  const auth = useAppSelector(selectAuth);
  const currentUser = auth.userInfo;

  // Get all users from preload data
  const preloadData = useAppSelector(selectPreloadData);
  const users = preloadData.users;

  // Get reservations, rooms, and types from Redux
  const reservations = useAppSelector(selectReservations);
  const rooms = useAppSelector(selectRooms);
  const types = useAppSelector(selectTypes);
  const reservation = reservations.data.find(reservation => reservation.id === reservationId);
  const isCreator = (reservation?.creator_id && currentUser?.id && reservation?.creator_id === currentUser?.id);
  const isParticipant = reservation?.participants.some(participant => participant.user.id === currentUser?.id);

  // Get messages and message operations
  const allMessages = useAppSelector(selectMessages);
  const messagesLoading = useAppSelector(selectMessagesLoading);
  const { createMessage, updateMessage, deleteMessage, requestFileUrl } = useMessageOperations();
  const messages = allMessages.data.filter(message => message.reservation_id === reservationId);
  const [editorPlainText, setEditorPlainText] = useState('');

  // File upload states
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [uploadedFilePath, setUploadedFilePath] = useState<string>("");
  const [uploadedFileName, setUploadedFileName] = useState<string>("");
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>("");
  
  // Separate messages and attachments
  const textMessages = messages.filter(message => !message.file_url || !message.content?.startsWith('File: '));
  const attachmentMessages = messages.filter(message => message.file_url && message.content?.startsWith('File: '));
  
  // Local states
  const [newMessage, setNewMessage] = useState<MessageInputState>({
    content: '',
    file: null
  });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Find reservation type name
  const typeName = types.data.find(type => type.id === reservation?.type_id)?.text || '';

  const typeInfo = types.data.find(type => type.id === reservation?.type_id);
  const typeColor = typeInfo?.color || 'darkgray';
  
  // Format date and time
  const formatDate = (date?: string) => date ? moment(date).format('MM/DD') : '';
  const formatTime = (time?: string) => time ? time.slice(0, 5) : '';
  const formatDay = (date?: string) => date ? moment(date).format('ddd') : '';
  
  // Get room names
  const roomNames = reservation?.room_ids
    .map(id => rooms.data.find(room => room.id === id)?.name)
    .filter(Boolean)
    .join(', ') || '';
  
  // Handle message text change
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(prev => ({
      ...prev,
      content: e.target.value
    }));
  };

  const handleMessageRefresh = () => {
    dispatch(fetchMessages());
  };
  
  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFiles(true);

    const response = await requestFileUrl({
      filename: file.name,
      file_type: file.type,
      acl: 'public-read'
    });

    // Extract the presigned URL and other data
    const { presigned_url, file_path, file_url, original_filename, disposition_filename } = response.payload.data;
    
    console.log("Got presigned URL:", presigned_url);

    setUploadedFilePath(file_path);
    setUploadedFileName(original_filename || file.name);
    setUploadedFileUrl(file_url);

    const uploadResponse = await fetch(presigned_url, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type,
        'x-amz-acl': 'public-read',
        //'Content-Disposition': `attachment; filename="${disposition_filename || original_filename || file.name}"`
        //'Content-Disposition': `attachment; filename*=UTF-8''${encodeURIComponent(disposition_filename || original_filename || file.name)}`
      },
      body: file
    });

    if (!uploadResponse.ok) {
      setUploadingFiles(false);
      throw new Error(`Upload failed with status: ${uploadResponse.status}`);
    }

    console.log("File uploaded successfully");

// Don't send a message as soon as a file is uploaded
    // Create a message with the file URL
    // If this is a new (not yet created) reservation, use tempReservationId
//    const messageResponse = await createMessage({
//      reservation_id: reservationId,
//      file_url: file_url,
//      content: newMessage.content || `File: ${file.name}`
//    });

    setUploadingFiles(false);

//    if (messageResponse.payload?.success) {
//      console.log("Message created successfully with file URL");
//      // Optional: clear the file input
//      if (e.target) {
//        e.target.value = '';
//      }
//    } else {
//      throw new Error('Failed to create message');
//    }
  };  

  // Handle submit message
  const handleSubmitMessage = async () => {
    if (!editorPlainText.trim() && !newMessage.file && !uploadedFileUrl) return;
    
    setUploadedFilePath("");
    setUploadedFileName("");
    setNewMessage(prev => ({ content: "", file: null }));

    try {
      setSendingMessage(true);
      await createMessage({
        reservation_id: reservationId,
        content: editorPlainText || ("File: " + uploadedFileName),
        file_url: uploadedFileUrl || undefined,
      });

      // Clear the input after sending
      setNewMessage({
        content: '',
        file: null
      });

      editor?.commands.clearContent();
      setEditorPlainText('');

      setUploadedFileUrl("");
      setUploadedFilePath("");
      setUploadedFileName("");

      // Refresh messages
      dispatch(fetchMessages());
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Format date range display
  const getDateTimeDisplay = () => {
    if (!reservationOccurrence) return '';
    const { startDate, endDate } = reservationOccurrence;
    const isSameDay = moment(startDate).isSame(endDate, 'day');

    if (isSameDay) {
      return `${moment(startDate).format('M/D (ddd) H:mm')} - ${moment(endDate).format('H:mm')}`;
    } else {
      return `${moment(startDate).format('M/D (ddd) H:mm')} - ${moment(endDate).format('M/D (ddd) H:mm')}`;
    }
  };
  
  // Extract filename from file URL
  const getFilenameFromUrl = (url: string) => {
    if (!url) return 'File';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

    // handle file downloads
  const handleFileDownload = (e: React.MouseEvent<HTMLAnchorElement>, fileUrl: string, fileName: string) => {
    e.preventDefault();
    
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName.replace(/^\d+_SCHEDULE_FILE_/, '');
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Fetch messages when component mounts
  useEffect(() => {
    dispatch(fetchMessages());
  }, [dispatch]);

  // after user posts a message, scroll messages container to the top
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = 0;
    }
  }, [textMessages.length]);

  const mentionCandidates = React.useMemo(() => {
    const participants = reservation?.participants ?? [];
    return participants
      .map(p => ({
        id: p.user.id,
        lastName: p.user.last_name ?? '',
        firstName: p.user.first_name ?? '',
        avatarPath: p.user.avatar?.path ?? '',
        label: `${p.user.last_name ?? ''} ${p.user.first_name ?? ''}`.trim(),
        isMe: p.user.id === currentUser?.id,
      }))
      .sort((a, b) => {
        if (a.isMe) return -1;
        if (b.isMe) return 1;
        return (a.id ?? 0) - (b.id ?? 0);
      });
  }, [reservation?.participants, currentUser?.id]);

  type MentionItem = {
    id: number;
    label: string;         // "last first"
    firstName: string;
    lastName: string;
    avatarPath: string;
    isMe: boolean;
  };

  function mentionFilter(items: MentionItem[], query: string) {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return items.slice(0, 5);

    const filtered = items.filter(u => {
      const hay = `${u.lastName} ${u.firstName}`.toLowerCase();
      const idStr = String(u.id);
      return hay.includes(q) || idStr.includes(q);
    });

    return filtered.slice(0, 5);
  }

  // A tiny dropdown list component rendered inside tippy
  const MentionList = React.forwardRef<
    { onKeyDown: (props: { event: KeyboardEvent }) => boolean },
    {
      items: MentionItem[];
      command: (item: MentionItem) => void;
    }
  >(function MentionListImpl({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = React.useState(0);

    const selectItem = (index: number) => {
      const item = items[index];
      if (item) command(item);
    };

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          selectItem(selectedIndex);
          return true;
        }
        if (event.key === 'Escape') {
          return false; // let tiptap close
        }
        return false;
      },
    }));

    // “ユーザーなし”
    if (items.length === 0) {
      return (
        <div style={{ padding: 8, fontSize: 12, color: '#666' }}>
          ユーザーなし
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 220 }}>
        {items.map((item, index) => (
          <div
            key={item.id}
            onMouseDown={(e) => e.preventDefault()} // keep focus in editor
            onClick={() => selectItem(index)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 10px',
              cursor: 'pointer',
              background: index === selectedIndex ? '#666' : 'transparent',
              fontSize: 13,
              borderRadius: 4,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: index === selectedIndex ? '#ddd' : 'transparent',
            }}
          >
            <Avatar
              src={item.avatarPath ? linkAvatar(item.avatarPath) : default_avatar}
              sx={{ width: 22, height: 22 }}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div>
                {item.label}
                {item.isMe ? <span style={{ marginLeft: 6, color: '#ccc' }}>[自分]</span> : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  });

  const editor = useEditor({
    extensions: [
      StarterKit,

      Mention.configure({
        HTMLAttributes: { class: 'mention-chip' },

        // This controls how mention becomes “text” when you call editor.getText()
        renderText({ node }) {
          const id = node.attrs.id;
          return `《{"user_id":${id}, "name":"${node.attrs.label}"}》`;
        },

        suggestion: {
          char: '@',
          items: ({ query }) => mentionFilter(mentionCandidates as MentionItem[], query),

          render: () => {
            let reactRenderer: ReactRenderer | null = null;
            let popup: TippyInstance[] | null = null;

            return {
              onStart: (props) => {
                reactRenderer = new ReactRenderer(MentionList, {
                  props: {
                    items: props.items,
                    command: (item: MentionItem) => props.command({ id: String(item.id), label: item.label }),
                  },
                  editor: props.editor,
                });

                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect as any,
                  appendTo: () => document.body,
                  content: reactRenderer.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: 'manual',
                  placement: 'bottom-start',
                });
              },

              onUpdate(props) {
                reactRenderer?.updateProps({
                  items: props.items,
                  command: (item: MentionItem) => props.command({ id: String(item.id), label: item.label }),
                });

                popup?.[0]?.setProps({
                  getReferenceClientRect: props.clientRect as any,
                });
              },

              onKeyDown(props) {
                // Forward to the list for ↑/↓/tab/enter
                // @ts-ignore
                return reactRenderer?.ref?.onKeyDown?.(props) ?? false;
              },

              onExit() {
                popup?.[0]?.destroy();
                reactRenderer?.destroy();
                popup = null;
                reactRenderer = null;
              },
            };
          },
        },
      }),
    ],
    editorProps: {
      attributes: {
        style: `
          height: 100%;
          min-height: 100%;
          outline: none;
          cursor: text;
          white-space: pre-wrap;
          word-break: break-word;
        `,
      },
    },
    content: '',
    onUpdate: ({ editor }) => {
      // store a plain-text version for submit
      setEditorPlainText(editor.getText());
    },
  }, [mentionCandidates]);

  // Check if reservation exists
  if (!reservation) {
    return (
      <div id="reservation-not-found">
        <Typography variant="h6">Reservation not found</Typography>
      </div>
    );
  }

  const editElement = (
    <>
      {/* Not creator: only Close */}
      {!isCreator && (
        <PassChangeButton
          onClick={handleClose}
          danger={false}
          style={{ width: "100%" }}
        >
          閉じる
        </PassChangeButton>
      )}

      {/* Creator, non-recurring: 2 buttons (Edit + Close) */}
      {isCreator && !reservation.recurring_frequency && (
        <>
            <PassChangeButton
              onClick={() => {handleEdit && handleEdit(null);}}
              danger={false}
              style={{ width: "100%" }}
            >
              編集
            </PassChangeButton>

            <PassChangeButton
              onClick={handleClose}
              danger={false}
              style={{ width: "100%" }}
            >
              閉じる
            </PassChangeButton>
        </>
      )}

      {/* Creator, recurring: 4 equal buttons sharing the space */}
      {isCreator && reservation.recurring_frequency && (
        <>
            <PassChangeButton
              onClick={() => handleEdit("this")}
              danger={false}
              style={{ width: "100%" }}
            >
              この日のみ編集
            </PassChangeButton>

            <PassChangeButton
              onClick={() => handleEdit("future")}
              danger={false}
              style={{ width: "100%" }}
            >
              今後の日を編集
            </PassChangeButton>

            <PassChangeButton
              onClick={() => handleEdit("all")}
              danger={false}
              style={{ width: "100%" }}
            >
              すべての日を編集
            </PassChangeButton>

            <PassChangeButton
              onClick={handleClose}
              danger={false}
              style={{ width: "100%" }}
            >
              閉じる
            </PassChangeButton>
        </>
      )}
    </>
  );

  return (
    <Grid container>
      <Grid item={true} xs={12}>
        <FormTitleHeader>
          <span>予定の詳細</span>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: (theme) => theme.palette.grey[700],
              fontSize: '24px',
              padding: 0,
              paddingLeft: '4px',
              paddingRight: '4px',
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            ×
          </IconButton>
        </FormTitleHeader>
      </Grid>

      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', padding: '10px 10px', width:"100%" }}>

            <Grid item={true} id="type-badge" xs={4}>
              <div style={{
                backgroundColor: typeColor,
                padding:"5px 5px 3px 5px",
                fontSize:"11px",
                borderRadius:"6px",
                display:"inline-block",
                color:"black",
                fontWeight:"bold",
                minWidth:"100px",
                textAlign:"center",
                border:"1px solid #ddd",
              }}>
              {typeName}
              </div>
            </Grid>
            <Grid item={true} id="type-badge" xs={8}>
              <div style={{display: "flex", flexDirection: "row"}}>{editElement}</div>
            </Grid>
      </div>

      <Grid item={true} container xs={12}
      sx={{
          p: { xs: 1, sm: 2 },
        }}
      >

        {/* Title Section */}
        <Grid container id="reservation-title-section">
          <Grid item xs={12}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', marginTop:"-1rem" }}>
              {reservation.is_private && <PrivateEventMark scale={0.75} />}
              {reservation.title}
            </div>
          </Grid>
        </Grid>

        {/* DateTime Section */}
        <Grid container id="reservation-datetime-section">
          <Grid item xs={12}>
            <div style={{ fontSize:"16px", fontWeight:"bold", marginTop:"5rem"}}>
              <TimeIcon /><span style={{marginLeft:"5px"}}>{getDateTimeDisplay()}</span>
            </div>
          </Grid>
        </Grid>

        {/* Location Section */}
        {roomNames && (
        <Grid container id="reservation-location-section">
          <Grid item xs={12}>
            <div style={{ fontSize:"16px", fontWeight:"bold", marginTop:"0.5rem"}}>
              <LocationIcon /><span style={{marginLeft:"5px"}}>{roomNames}</span>
            </div>
          </Grid>
        </Grid>
        )}

        <StyledDivider style={{marginTop:"1rem"}}/>

        {/* Description and Attachments Section - Side by Side */}
        <Grid container id="reservation-content-section"
            sx={{
              fontSize: { xs: 16, sm: 14 },
            }}
          >
          {/* Description on the left */}
          <Grid item={true} xs={12} md={8} id="reservation-description-section" >
            <Grid item={true} id="reservation-description"
             sx={{
                pr: { xs:0, sm:2},
                whiteSpace: 'pre-line'
              }}
              >
              {reservation.description}
            </Grid>
          </Grid>

          {/* Attachments on the right */}
          <Grid item={true} xs={12} md={4} id="reservation-attachments-section" sx={{
            borderTop: { xs:"1px solid #ddd", sm:"none" },
            mt: { xs:"1rem", sm:"0" },
            pt: { xs:"1rem", sm:"0" },
            borderLeft: { sm:"1px solid #ddd", xs:"none" },
            paddingLeft: { sm:"1rem", xs:"0" },
          }}>
            <div id="section-label" style={{ fontWeight:"bold"}}>ファイル {messagesLoading && " (Loading)"}</div>
              <div id="reservation-attachments" style={{ marginTop:"1rem"}}>
                {attachmentMessages.length > 0 ? 
                  attachmentMessages.map((message, index) => {
                    const fileName = getFilenameFromUrl(message.file_url || '');
                    const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);

                    const fileUrl = getFormattedFileUrl(message.file_url || '');
                    const cleanFileName = fileName.replace(/^\d+_SCHEDULE_FILE_/, '');

                    return (
                      <div key={index} style={{display:"flex", flexDirection:"row", alignItems:"center", gap: "1rem"}}>
                        {/* Keeping ability to delete files inside the edit dialog and disabling here 
                        <div style={{border: "1px solid #800", borderRadius:"0.5rem", padding: "0.5rem", margin: "0.5rem", backgroundColor:"#fbb"}} onClick={() => {deleteMessage(message.id);}}>X</div>*/}
                        <a 
                          href={fileUrl} 
                          onClick={(e) => handleFileDownload(e, fileUrl, fileName)}
                          id={`attachment-file-${index}`} 
                          className="attachment-file" 
                          key={index} 
                          style={{
                            display: "flex",
                            width:"100%",
                            borderRadius: "6px", 
                            border: "1px solid wheat",
                            padding: "5px 10px",
                            margin: "0.5rem 0",
                            textDecoration: "none",
                            color: "inherit",
                            cursor: "pointer",
                            background: "ivory",
                            alignItems: "center"
                          }}
                        >
                          {isImage ? <IconFileImage style={{ marginRight: '8px', width: '20px', height: '20px' }} /> : <IconFileFile style={{ marginRight: '8px', width: '20px', height: '20px' }} />}
                          <div id="file-info" style={{ fontSize: "12px"}}>
                            <div id="file-name" style={{display: "none"}}>
                              {fileName}
                            </div>
                            {message.content && (
                              <div id="file-description">
                                {message.content.replace(/^File:\s*/i, '')}
                              </div>
                            )}
                            <div id="file-uploader" style={{ display: "none"}}>
                              {users.find(user => user.id === message.creator_id)?.last_name}, 
                              {users.find(user => user.id === message.creator_id)?.first_name} -
                              {moment(message.created_at).format('MM/DD HH:mm')}
                            </div>
                          </div>
                        </a>
                        {(isParticipant ?
                          <div
                            onClick={() => { if (window.confirm("ファイルを削除しますか?")) { deleteMessage(message.id);} }}
                            style={{display:"flex", color:"#fff", backgroundColor:"#666", alignItems:"center", justifyContent: "center", height: "3rem", width: "3rem", borderRadius:"50%", border: "1px solid #888", cursor: "pointer"}}
                          >✖</div> :
                          null
                        )}
                      </div>
                    );
                  }) : (
                  <div id="no-attachments">添付ファイルはありません</div>
                )
              }
              {/* Add attachment button */}
              <div id="add-attachment-button" style={{ display: "none"}}>
                <label htmlFor="file-input" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px dashed #ccc', padding: '0.5rem', borderRadius: '1rem' }}>
                  ファイルを追加 (Drag / Drop or Click)
                </label>
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </div>
            </div>
          </Grid>
        </Grid>

        <StyledDivider/>

        {/* Participants Section */}
        <Grid container item={true} id="reservation-participants-section" sx={{ mt: 1}}>
          <Grid item={true} xs={12}>
            <div id="section-label" style={{fontSize:"14px",fontWeight:"bold"}}>参加者</div>
            <Grid item={true} id="participants-list" sx={{ display: 'flex', flexDirection: 'column', padding: '1rem', border: '1px solid #ccc', borderRadius: '1rem', overflowY: "auto", height:{xs: "150px", sm:"200px"} }}>
              {[...reservation.participants]
                .sort((a, b) => {
                  if (a.user.id === currentUser?.id) return -1;
                  if (b.user.id === currentUser?.id) return 1;
                  return (a.user.id || 0) - (b.user.id || 0);
                })
                .map((participant, index) => (
                  <div id={`participant-item-${index}`} className="participant-item" key={index} style={{display:"flex", marginBottom:"1rem"}}>
                    <Avatar
                      className="participant-avatar"
                      src={
                        participant?.user?.avatar?.path
                          ? linkAvatar(participant.user?.avatar?.path)
                          : default_avatar
                      }
                      sx={{ width: 24, height: 24 }}
                    />
                    <div className="participant-name" style={{ fontSize:"14px", marginLeft:"1rem"}}>
                      {`${participant.user.last_name} ${participant.user.first_name}`}
                      {participant.user.id === currentUser?.id &&
                        <span className="current-user-indicator"> [自分]</span>
                      }
                    </div>
                  </div>
                ))}
              </Grid>
          </Grid>
        </Grid>

        <StyledDivider/>

        {/* Messages Section */}
        <Grid container id="reservation-messages-section" style={{display: (isCreator || isParticipant) ? undefined : "none"}} >
          <Grid item={true} xs={12}>
            
            <div id="section-label" style={{marginTop: "0", fontSize: "14px", fontWeight: "bold"}} >メッセージ</div>
            
            <div id="messages-container" ref={messagesContainerRef} style={{ padding: '1rem', backgroundColor: "#F2F2F2", borderRadius: '1rem', maxHeight: '300px', overflowY: 'auto', marginTop:"5px" }}>
              {textMessages.length === 0 ? (
                <div id="no-messages" style={{fontSize:"14px", color:"darkgray"}}>
                  コメントはありません
                </div>
              ) : (
                textMessages.map((message, index) => {
                  const sender = users.find(user => user.id === message.creator_id);
                  const isCurrentUser = message.creator_id === currentUser?.id;
                  const fileName = getFilenameFromUrl(message.file_url || '');
                  const isImage = /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
                  const fileUrl = getFormattedFileUrl(message.file_url || '');

                  return (
                    <Grid container
                      id={`message-item-${index}`}
                      className={`message-item ${isCurrentUser ? 'current-user-message' : ''}`}
                      key={message.id}
                      sx={{ background:"transparent",
                        paddingTop: (index === 0) ? "0px" : "10px",
                        paddingBottom:"10px",
                        borderBottom:"1px solid #ddd"
                      }}
                    >
                    
                        <Grid item={true} xs={1.4} sm={0.58} className="message-avatar" >
                          <Avatar
                            src={
                              sender?.avatar?.path
                                ? linkAvatar(sender.avatar.path)
                                : default_avatar
                            }
                            sx={{ width: 32, height: 32 }}
                          />
                        </Grid>

                        <Grid xs={9.8} sm={11} item={true} className="message-content" sx={{fontSize:{ xs:"16px", sm:"14px"}}} >
                          <div className="message-header">
                            <div className="message-sender">
                              <strong>{`${sender?.last_name || ''} ${sender?.first_name || ''}`}</strong>
                            </div>
                            <div className="message-time" style={{color:"gray", fontSize:"12px"}}>
                              {moment(message.created_at).format('M月D日 | HH:mm')}
                            </div>
                          </div>
                          <div className="message-text" style={{marginTop:"8px", whiteSpace:"pre-line"}}>
                            {RenderMessageContent(message.content || "")}
                          </div>

                            {/* ATTACHMENTS inside comment */}
                           {message.file_url && (
                          <div key={index}
                          style={{
                            display:"flex",
                          flexDirection:"row",
                          alignItems:"start",
                          width:"100%",
                          marginBottom:"0",
                          paddingBottom:"0",
                          marginTop:"10px",
                          gap:"10px"
                        }}>
                            {/* Keeping ability to delete files inside the edit dialog and disabling here 
                            <div style={{border: "1px solid #800", borderRadius:"0.5rem", padding: "0.5rem", margin: "0.5rem", backgroundColor:"#fbb"}} onClick={() => {deleteMessage(message.id);}}>X</div>*/}
                            <a 
                              href={fileUrl} 
                              onClick={(e) => handleFileDownload(e, fileUrl, fileName)}
                              id={`attachment-file-${index}`} 
                              className="attachment-file" 
                              key={index} 
                              style={{
                                display: "flex",
                                width:"fit-content",
                                borderRadius: isImage ? "0" : "6px",
                                border: isImage ? "none" : "1px solid wheat",
                                padding:isImage ? 0 : "5px",
                                textDecoration: "none",
                                color: "inherit",
                                cursor: "pointer",
                                background: isImage ? "none" : "ivory",
                                alignItems: "start"
                              }}
                            >
                              {isImage && (<img src={fileUrl} alt={fileName} style={{ maxHeight: '100px', maxWidth: '100px', objectFit: 'contain', border:"1px solid darkgray" }} />)}

                              {isImage ? null : <IconFileFile style={{ marginRight: '8px', width: '20px', height: '20px' }} />}

                              <div id="file-info" style={{ fontSize: "12px", display: "flex", flexDirection: "row", alignItems: "center", gap: "8px"}}>
                                <div id="file-name" style={{display: "none"}}>
                                  {fileName}
                                </div>
                                {message.content && (
                                  !isImage && (
                                  <div id="file-description">
                                    {fileName.replace(/^\d+_SCHEDULE_FILE_/, '')}
                                  </div>
                                  )
                                )}
                                <div id="file-uploader" style={{ display: "none"}}>
                                  {users.find(user => user.id === message.creator_id)?.last_name}, 
                                  {users.find(user => user.id === message.creator_id)?.first_name} -
                                  {moment(message.created_at).format('MM/DD HH:mm')}
                                </div>
                                
                              </div>
                            </a>
                          </div>)}

                        </Grid>
                      
                      

                      <div
                        className="message-delete"
                        onClick={() => deleteMessage(message.id)}
                        style={{
                          display: isCurrentUser ? 'flex' : "none",
                          alignItems: 'center',   
                          justifyContent: 'center',
                          width: '20px',        
                          height: '20px',  
                          minWidth: '20px',        
                          minHeight: '20px',      
                          padding: '0',          
                          fontSize: '12px',  
                          fontWeight: "bold",     
                          borderRadius: '50%',    
                          backgroundColor: '#888',
                          color: 'white',
                          cursor: 'pointer',
                          alignSelf: 'flex-start', 
                          boxSizing: 'border-box',
                        }}
                      >
                        ×
                      </div>
                    </Grid>
                  );
                })
              )}
            </div>

            {/* Message Input Section */}
            <Grid container item={true} id="message-input-container" sx={{ mt: {xs: 1, sm: 2 } }}>
              {/*<AppTextArea
                name="description"
                value={newMessage.content}
                onChangeEvent={handleMessageChange}
                rows={3}
              />*/}
              <div
                style={{
                  width: '100%',
                  border: '1px solid #ccc',
                  borderRadius: '12px',
                  padding: '10px 12px',
                  background: '#fff',
                  height: "12rem",
                }}
              >
                <EditorContent style={{height: "100%"}} editor={editor} />
              </div>
              <input type="file"
                id="file-input"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="file-input" style={{ marginTop:"1rem",fontSize:"12px",cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', border: '1px dashed #ccc', padding: '1rem', borderRadius: '1rem' }}>
                          <IconPlus style={{ marginRight: '8px', width: '20px', height: '20px' }}/>ファイルを追加
                        </label>
              
              {uploadedFileName && (
                <>
                  <div style={{ margin: '10px', color: 'inherit', fontSize: '12px' }}>
                    {`${uploadedFileName}`}
                  </div>
                  <div style={{ margin: '10px', color: 'red', fontSize: '12px' }} onClick={() => {
                    setUploadedFilePath("");
                    setUploadedFileName("");
                    setNewMessage(prev => ({
                      ...prev,
                      file: null
                    }));
                  }}>
                    削除
                  </div>
                </>
              )}
              <Grid item={true} id="message-actions" xs={12} style={{ marginTop: "1rem", display: "flex" }}>
                <PassChangeButton
                  onClick={handleSubmitMessage}
                  style={{width:"100%"}}
                  disabled={sendingMessage || (!editorPlainText.trim() && !newMessage.file && !uploadedFileUrl)}
                >コメント投稿
                  {sendingMessage ? "送信中..." : ""}
                </PassChangeButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};


const LocationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 64 64" fill="none">
<path fill="#000000" d="M32,0C18.746,0,8,10.746,8,24c0,5.219,1.711,10.008,4.555,13.93c0.051,0.094,0.059,0.199,0.117,0.289l16,24
	C29.414,63.332,30.664,64,32,64s2.586-0.668,3.328-1.781l16-24c0.059-0.09,0.066-0.195,0.117-0.289C54.289,34.008,56,29.219,56,24
	C56,10.746,45.254,0,32,0z M32,32c-4.418,0-8-3.582-8-8s3.582-8,8-8s8,3.582,8,8S36.418,32,32,32z"/>
</svg>
);

const TimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none">
<path fillRule="evenodd" clipRule="evenodd" d="M12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4ZM2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.8284 6.75736C12.3807 6.75736 12.8284 7.20507 12.8284 7.75736V12.7245L16.3553 14.0653C16.8716 14.2615 17.131 14.8391 16.9347 15.3553C16.7385 15.8716 16.1609 16.131 15.6447 15.9347L11.4731 14.349C11.085 14.2014 10.8284 13.8294 10.8284 13.4142V7.75736C10.8284 7.20507 11.2761 6.75736 11.8284 6.75736Z" fill="#000000"/>
</svg>
);



const FormTitleHeader = scStyled.div`
  background-color: ${(p) => p.theme.colors.lightgray09};
  padding:0.5rem;
  padding-left:1rem;
  font-size:14px;
  font-weight:bold;
  text-align:left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
    top: 0;
    z-index: 10;
    border-bottom:1px solid #bbb;
    width:100%;
    padding-right:1rem;
`;

const StyledDivider = scStyled.div`
  border-top:1px solid ${(p) => p.theme.colors.lightgray09};
  width: 100%; 
  margin:1rem 0;
  padding:0;
`;

export default ReservationInfo;

// Handle file URL formatting similar to your existing function
const getFormattedFileUrl = (url) => {
  let baseUrl = process.env.REACT_APP_S3_BUCKET_URL || 'https://livelet-stage.s3.ap-northeast-3.amazonaws.com';
  const normalizedBaseUrl = baseUrl.replace(/\/+$/, "");
  
  const urlRegex = /^(https?:\/\/)?([^/]+)(\/.+)?$/;
  const match = url.match(urlRegex);
  if (match && match[3]) {
    const path = match[3] || '';
    const normalizedPath = path.replace(/^\/+/, "");
    return `${normalizedBaseUrl}/${normalizedPath}`;
  }
  
  const normalizedUrl = url.replace(/^\/+/, "");
  return `${normalizedBaseUrl}/${normalizedUrl}`;
};
